import React from 'react';
import {
  createCrudMethodsListConfig,
  createFiltersPanelListConfig,
  createMethodApiCreateListConfig,
  createSortingMethodListConfig,
} from '@prometheus/utils';

import {
  createDataSource,
  getCdmDataSourcesList,
  getCdmDataSourcesListFilters,
  getSorting,
  removeDataSource,
} from '@/api';

import { ListConfigInterface } from '@prometheus/types';
import { Button, ColumnDef, Icon, StatusTag } from '@prometheus/components';
import {
  AUTHOR_COLUMN,
  CREATED_COLUMN,
  NameCell,
  UPDATED_COLUMN,
} from '@prometheus/features';
import { CdmDataSourceDTO } from '@/types';

const DATABASE_SEARCH_FILTER = 'filter.search';

const getCdmDataSourcesListFiltersWithSearch = async (options: string) => {
  const filters = await getCdmDataSourcesListFilters(options);
  const items = filters.items || [];

  if (items.some((item: any) => item.key === DATABASE_SEARCH_FILTER)) {
    return filters;
  }

  return {
    ...filters,
    items: [
      {
        key: DATABASE_SEARCH_FILTER,
        options: [],
      },
      ...items,
    ],
  };
};

export const databasesConfig: ListConfigInterface<
  any,
  any,
  ColumnDef<CdmDataSourceDTO, any>
> = {
  rowId: 'id',
  listInitialSort: null,
  iconName: 'dataCatalog',
  createEntityModal: {
    name: 'create-cdm',
    createMethodApi: (typeContainer, parentId) => {
      return createMethodApiCreateListConfig<any, any>(
        typeContainer,
        parentId,
        createDataSource
      );
    },
  } as any,
  createCrudMethod: (typeContainer, parentId) => {
    return createCrudMethodsListConfig<any>(
      typeContainer,
      parentId,
      getCdmDataSourcesList,
      removeDataSource
    );
  },
  createFiltersPanel: (typeContainer, parentId) => {
    return createFiltersPanelListConfig(
      null,
      null,
      getCdmDataSourcesListFiltersWithSearch
    );
  },
  createSortingMethod() {
    return createSortingMethodListConfig(null, {
      getSorting: getSorting,
    });
  },
  getCols: (settings: any) => {
    return [
      {
        header: settings.t('tables.cols.name'),
        accessorKey: 'name',
        id: 'name',
        size: 200,
        cell: NameCell,
      },
      {
        header: settings.t('tables.cols.dbms_type'),
        accessorKey: 'type',
        id: 'type',
        size: 110,
      },
      {
        header: settings.t('tables.cols.database'),
        accessorKey: 'connectionString',
        id: 'connectionString',
        size: 300,
      },
      {
        header: settings.t('tables.cols.cdmSchema'),
        accessorKey: 'cdmSchema',
        id: 'cdmSchema',
        size: 100,
      },
      // {
      //   header: settings.t('tables.cols.modelType'),
      //   accessorKey: 'modelType',
      //   id: 'modelType',
      //   width: '5%',
      //   minWidth: 80,
      // },
      {
        ...CREATED_COLUMN({
          header: settings.t('tables.cols.created'),
        }),
      },
      {
        ...UPDATED_COLUMN({
          header: settings.t('tables.cols.updated'),
        }),
      },
      {
        ...AUTHOR_COLUMN({
          header: settings.t('tables.cols.author'),
        }),
      },
      {
        header: '',
        accessorKey: 'published',
        id: 'published',
        size: 120,
        enableResizing: false,
        cell: props => {
          return !props.getValue() || props.getValue().length === 0 ? (
            <Button
              size="small"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                settings?.onPublish(props.row.original.id);
              }}
              color="info"
              variant="outlined"
              startIcon={<Icon iconName="publish" />}
              sx={{
                px: 2,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: 12,
                minWidth: 98,
              }}
            >
              {settings.t('buttons.publish')}
            </Button>
          ) : (
            <StatusTag
              text={settings.t('statuses.published')}
              color="success"
              align="center"
              light
            />
          );
        },
      },
    ];
  },
};
